/**
 * Settings Page
 * 
 * Comprehensive settings interface for the Prelude application.
 * Provides configuration options for both user preferences and
 * system-wide settings (for admin users).
 */

import React, { useState } from 'react';
import {
  Box,
  Container,
  Tab,
  Tabs,
  Typography,
  Paper,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import { customFetch } from '../utils/customFetch';
import PageHeader from '../components/common/PageHeader';
import ProfileSettings from '../components/settings/ProfileSettings';
import NotificationSettings from '../components/settings/NotificationSettings';
import ChurchSettings from '../components/settings/ChurchSettings';
import BillingSettings from '../components/settings/BillingSettings';

// Tab panel component for settings sections
function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`settings-tabpanel-${index}`}
      aria-labelledby={`settings-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function Settings() {
  const [selectedTab, setSelectedTab] = useState(0);
  const { user } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isAdmin = user?.role === 'admin';
  const { t } = useTranslation();

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
  };

  const tabs = [
    { label: t('settings.tabs.profile'), component: <ProfileSettings /> },
    { label: t('settings.tabs.notifications'), component: <NotificationSettings /> },
    ...(isAdmin ? [
      { label: t('settings.tabs.church'), component: <ChurchSettings /> },
      { label: t('settings.tabs.billing'), component: <BillingSettings /> }
    ] : [])
  ];

  return (
    <Box sx={{ 
      p: { xs: 0.5, sm: 2, md: 3 },
      width: '100%',
      maxWidth: '100vw',
      overflowX: 'hidden'
    }}>
      <PageHeader 
        title={t('settings.title')} 
        subtitle={isAdmin ? t('settings.subtitle.admin') : t('settings.subtitle.user')}
      />
      <Paper 
        sx={{ 
          p: { xs: 0.5, sm: 2, md: 3 },
          width: '100%',
          maxWidth: '100%',
          overflowX: 'hidden'
        }}
      >
        <Box sx={{ 
          borderBottom: 1, 
          borderColor: 'divider',
          mb: 2,
          width: '100%'
        }}>
          <Tabs
            value={selectedTab}
            onChange={handleTabChange}
            variant={isMobile ? 'scrollable' : 'standard'}
            scrollButtons="auto"
            allowScrollButtonsMobile
            aria-label="settings tabs"
            sx={{
              width: '100%',
              '.MuiTabs-flexContainer': {
                gap: { xs: 0.5, sm: 2 }
              },
              '.MuiTab-root': {
                minWidth: { xs: 'auto', sm: 120 },
                px: { xs: 1, sm: 2 },
                fontSize: { xs: '0.875rem', sm: '1rem' }
              }
            }}
          >
            {tabs.map((tab, index) => (
              <Tab
                key={tab.label}
                label={tab.label}
                id={`settings-tab-${index}`}
                aria-controls={`settings-tabpanel-${index}`}
              />
            ))}
          </Tabs>
        </Box>

        <Box sx={{ 
          width: '100%',
          '& > div': {
            width: '100%',
            maxWidth: '100%'
          }
        }}>
          {tabs.map((tab, index) => (
            <TabPanel 
              key={tab.label} 
              value={selectedTab} 
              index={index}
              sx={{
                p: { xs: 0.5, sm: 2, md: 3 },
                width: '100%'
              }}
            >
              {tab.component}
            </TabPanel>
          ))}
        </Box>
      </Paper>
    </Box>
  );
}

export default Settings;
